import DirectionsBoatOutlinedIcon from '@mui/icons-material/DirectionsBoatOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Stack,
} from '@mui/material';
import { useCallback, useRef } from 'react';

import ScenarioModeAlert from '../../../../../shared/components/display/ScenarioModeAlert';
import MBModal from '../../../../../shared/components/modals/MBModal';
import useScenarioMode from '../../../../../shared/hooks/useScenarioMode';

import useGroupEditScenarioData from '../../../hooks/useGroupEditScenarioData';
import useGroupEditScenarioOverlay from '../../../hooks/useGroupEditScenarioOverlay';
import useGroupEditSubmit from '../../../hooks/useGroupEditSubmit';

import type { GroupEditModalProps } from './groupEditTypes';

import RHFVesselForm from '../../schedule/components/RHFVesselForm';

export type {
  GroupEditModalProps,
  VesselModalMode,
  VoyageRow,
} from './groupEditTypes';

// ── Component ────────────────────────────────────────────────

const noop = () => undefined;

const GroupEditModal = ({
  open,
  shipGroup,
  voyageRow,
  mode = 'group-edit',
  readOnly = false,
  onClose,
  setSnackbarOpen = noop,
  setSnackbarMessage = noop,
  setSnackbarSeverity = noop,
}: GroupEditModalProps) => {
  const isCreateMode = mode === 'create';
  const submitRef = useRef<HTMLFormElement | null>(null);
  const { isScenarioActive, scenarioLabel } = useScenarioMode();

  const showSnackbar = useCallback(
    (
      message: string,
      severity: 'success' | 'error' | 'warning' | 'info' = 'error'
    ) => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    },
    [setSnackbarMessage, setSnackbarSeverity, setSnackbarOpen]
  );

  // ── Data loading + scenario delta maps ──
  const {
    freshGroup,
    baseGroup,
    departurePorts,
    isLoading,
    deltaJsonMap,
    voyageChangeTypeMap,
    shipmentDeltaByShipmentId,
    shipmentChangeTypeById,
    createdShipmentsByVoyageDeltaId,
    createdShipmentsByVoyageId,
    createdVoyageDeltas,
    voyageDeltaIdByVoyageId,
    groupShipmentDeltaIds,
  } = useGroupEditScenarioData({
    open,
    isCreateMode,
    shipGroup,
    voyageRow,
    showSnackbar,
    onClose,
  });

  // ── Overlay: merge delta maps onto freshGroup to produce editData ──
  const {
    editData,
    voyageChangedFieldsMap,
    shipmentChangedFieldsMap,
    topLevelChangedFields,
  } = useGroupEditScenarioOverlay({
    freshGroup,
    baseGroup,
    isCreateMode,
    isScenarioActive,
    deltaJsonMap,
    voyageChangeTypeMap,
    shipmentDeltaByShipmentId,
    shipmentChangeTypeById,
    createdVoyageDeltas,
    createdShipmentsByVoyageDeltaId,
    createdShipmentsByVoyageId,
  });

  // ── Submit + undo handlers ──
  const { handleSubmit, handleUndoGroupDeltas, isSubmitting, isUndoing } =
    useGroupEditSubmit({
      isCreateMode,
      freshGroup,
      editData,
      deltaJsonMap,
      voyageChangeTypeMap,
      voyageDeltaIdByVoyageId,
      createdVoyageDeltas,
      groupShipmentDeltaIds,
      showSnackbar,
      onClose,
    });

  // ── Derived render state ──
  const hasGroupDeltas =
    isScenarioActive &&
    !isCreateMode &&
    (Object.keys(voyageDeltaIdByVoyageId).length > 0 ||
      createdVoyageDeltas.length > 0 ||
      groupShipmentDeltaIds.length > 0);

  const isBusy = isSubmitting || isUndoing;
  const isEditReady = isCreateMode || (!isLoading && freshGroup && editData);
  const baseTitle = isCreateMode
    ? 'Add New Ship'
    : readOnly
      ? 'View Voyage Group'
      : 'Edit Voyage Group';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const title: any = isScenarioActive ? (
    <Stack direction='row' alignItems='center' spacing={1}>
      <span>{baseTitle}</span>
      <Chip
        icon={<ScienceOutlinedIcon />}
        label={scenarioLabel('Scenario Mode')}
        color='warning'
        size='small'
      />
    </Stack>
  ) : (
    baseTitle
  );

  const submitLabel = isCreateMode
    ? scenarioLabel('Add Ship -')
    : scenarioLabel('Update Voyage Group');

  const formMode = isCreateMode ? 'create' : 'group-edit';

  return (
    <MBModal
      open={open}
      onClose={onClose}
      disableClose={isBusy || isLoading}
      fullWidth
      maxWidth='md'
      title={title}
      contentText={
        isCreateMode
          ? 'Fill in the details to add a new ship to the schedule'
          : ''
      }
      titleIcon={<DirectionsBoatOutlinedIcon fontSize='medium' />}
      isLoading={isLoading}
      actions={
        <Stack direction='row' spacing={2} sx={{ padding: '20px 24px' }}>
          <Button
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disableElevation
            disabled={isBusy || isLoading}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </Button>

          {!readOnly && hasGroupDeltas && (
            <Button
              variant='outlined'
              color='warning'
              onClick={handleUndoGroupDeltas}
              disableElevation
              disabled={isBusy || isLoading}
              startIcon={
                isUndoing ? (
                  <CircularProgress size={18} color='inherit' />
                ) : (
                  <ReplayOutlinedIcon />
                )
              }
            >
              Undo Scenario Changes
            </Button>
          )}

          {!readOnly && (
            <Button
              variant='contained'
              onClick={() => submitRef.current?.requestSubmit()}
              disableElevation
              type='submit'
              disabled={isBusy || isLoading}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color='inherit' />
                ) : undefined
              }
            >
              {submitLabel}
            </Button>
          )}
        </Stack>
      }
    >
      <Box sx={{ minHeight: 300 }}>
        <ScenarioModeAlert />

        {isEditReady ? (
          <Fade in timeout={300}>
            <div>
              <RHFVesselForm
                onSubmit={handleSubmit}
                ref={submitRef}
                editData={editData}
                mode={formMode}
                readOnly={readOnly}
                prefetchedDeparturePorts={
                  !isCreateMode ? departurePorts : undefined
                }
                voyageChangedFieldsMap={
                  isScenarioActive ? voyageChangedFieldsMap : undefined
                }
                shipmentChangedFieldsMap={
                  isScenarioActive ? shipmentChangedFieldsMap : undefined
                }
                topLevelChangedFields={
                  isScenarioActive ? topLevelChangedFields : undefined
                }
                scenarioCancelledVoyageIds={
                  isScenarioActive
                    ? new Set(
                        Object.entries(voyageChangeTypeMap)
                          .filter(([, ct]) => ct === 'CANCELLED')
                          .map(([vid]) => vid)
                      )
                    : undefined
                }
              />
            </div>
          </Fade>
        ) : (
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{ minHeight: 300 }}
          >
            <CircularProgress />
          </Stack>
        )}
      </Box>
    </MBModal>
  );
};

export default GroupEditModal;
